import type { TableDataHook } from "@/features/integrations/shared/renderer/hooks/types";

/**
 * Getter that optionally returns a table data hook based on context (e.g. clientId).
 * Allows integrations to opt out for certain views (e.g. tenant list vs client detail).
 */
export type TableDataHookGetter = (
  clientId?: string
) => TableDataHook | undefined;

const tableDataHooksRegistry = new Map<string, TableDataHookGetter>();

/**
 * Register a table data hook for a specific integration in the job-sync module.
 * @param integrationName - The integration name (e.g., "indeed", "werkzoeken")
 * @param getter - Returns the hook when applicable, or undefined to fall back to context data
 */
export function registerJobSyncTableDataHook(
  integrationName: string,
  getter: TableDataHookGetter
): void {
  const normalizedName = integrationName.toLowerCase();
  tableDataHooksRegistry.set(normalizedName, getter);
}

/**
 * Get the registered table data hook for an integration.
 * @param integrationName - The integration name
 * @param clientId - Optional client ID passed to the integration's getter
 * @returns The hook function, or undefined if not registered or not applicable to the current view
 */
export function getJobSyncTableDataHook(
  integrationName: string | undefined,
  clientId?: string
): TableDataHook | undefined {
  if (!integrationName) return undefined;
  const normalizedName = integrationName.toLowerCase();
  const getter = tableDataHooksRegistry.get(normalizedName);
  return getter?.(clientId);
}
